import React from 'react';
import { motion } from 'framer-motion';
import { Box, Container, Typography, Grid, Paper, Card, CardContent, List, ListItem, ListItemIcon, ListItemText, Divider, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Accordion, AccordionSummary, AccordionDetails, Button } from '@mui/material';
import VerifiedIcon from '@mui/icons-material/Verified';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import ScienceIcon from '@mui/icons-material/Science';
import ContentPasteSearchIcon from '@mui/icons-material/ContentPasteSearch';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';


const Quality = () => {
    const qualityStats = [
        { label: 'Defect Rate', value: '< 50 PPM', desc: 'Parts Per Million' },
        { label: 'On-Time Delivery', value: '98.5%', desc: 'Consistency' },
        { label: 'First Pass Yield', value: '99.2%', desc: 'Efficiency' },
        { label: 'Audit Score', value: '100%', desc: 'Compliance' },
    ];

    const equipmentList = [
        { name: 'Coordinate Measuring Machine (CMM)', make: 'Carl Zeiss / Contura', range: '700x1000x600 mm', accuracy: '1.8 µm + L/300', desc: 'Programmable CNC CMM for complex 3D geometry verification.' },
        { name: 'Vision Measuring System (VMS)', make: 'Micro-Vu', range: '300x300x200 mm', accuracy: '2.5 µm', desc: 'Non-contact measurement for delicate and small components.' },
        { name: 'Profile Projector', make: 'Mitutoyo PH-3515', range: '10x - 50x Magnification', accuracy: '0.1%', desc: 'Shadow graph inspection for profiles and contours.' },
        { name: 'Digital Height Gauge', make: 'Trimos V4', range: '0-600 mm', accuracy: '2 µm', desc: '2D measurement system with air cushion movement.' },
        { name: 'Surface Roughness Tester', make: 'Mitutoyo Surftest SJ-210', range: 'Ra, Rz, Rq, Rt', accuracy: '0.01 µm', desc: 'Portable tester for measuring surface finish on machine.' },
        { name: 'Hardness Tester', make: 'Rockwell', range: 'HRC, HRB', accuracy: '± 1.0 HR', desc: 'Verification of heat treatment and material hardness.' },
        { name: 'Bore Gauge Sets', make: 'Mitutoyo / Baker', range: '6 mm - 150 mm', accuracy: '1 µm', desc: 'Precision ID measurement for tight tolerance bores.' },
        { name: 'Thread Plug & Ring Gauges', make: 'Baker / Hippie', range: 'M2 to M100', accuracy: '6H / 6g', desc: 'Go/No-Go verification for threaded features.' },
        { name: 'Slip Gauge Blocks', make: 'Grade 0', range: 'Set of 112 Pcs', accuracy: 'Grade 0', desc: 'Master standards for calibration and setting gauges.' }
    ];

    const workFlow = [
        { title: 'Incoming Quality Control (IQC)', icon: <ContentPasteSearchIcon fontSize="large" />, desc: 'Every batch of raw material is verified against Mill Test Certificates (MTC). We perform visual inspection, dimensional checks, and PMI (Positive Material Identification) to ensure material grade authenticity before it enters our stores.' },
        { title: 'In-Process Quality Control (IPQC)', icon: <PrecisionManufacturingIcon fontSize="large" />, desc: 'Operators perform hourly inspections using calibrated gauges. Patrol inspectors conduct random spot checks. Detailed control charts (X-bar & R Charts) are maintained for critical dimensions to monitor process stability and prevent deviations.' },
        { title: 'Final Quality Control (FQC)', icon: <VerifiedIcon fontSize="large" />, desc: '100% visual inspection is performed to ensure zero cosmetic defects. Critical dimensions are verified using CMM and documented. A pre-dispatch audit ensures packaging meets customer specifications to prevent transit damage.' },
        { title: 'Standardization & Calibration', icon: <ScienceIcon fontSize="large" />, desc: 'All measuring instruments are calibrated at defined intervals by NABL accredited laboratories. Master samples and limit samples are maintained to ensure inspection consistency across shifts.' }
    ];

    return (
        <Box>
            {/* Hero Section */}
            <Box sx={{
                bgcolor: '#1a237e',
                color: 'white',
                py: { xs: 8, md: 16 },
                textAlign: 'center',
                backgroundImage: 'linear-gradient(rgba(0,0,0,0.8), rgba(0,0,0,0.7)), url(/images/quality.png)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundAttachment: 'fixed',
                position: 'relative'
            }}>
                <Container maxWidth="lg">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                    >
                        <Typography variant="overline" letterSpacing={3} sx={{ color: '#64b5f6', fontWeight: 'bold' }}>
                            AYDA QUALITY ASSURANCE
                        </Typography>
                        <Typography variant="h1" component="h1" fontWeight="900" sx={{ fontSize: { xs: '2.5rem', md: '4.5rem' }, mb: 2, textShadow: '2px 2px 10px rgba(0,0,0,0.5)' }}>
                            Precision is Our Promise
                        </Typography>
                        <Typography variant="h4" sx={{ opacity: 0.9, fontWeight: 300, maxWidth: '800px', mx: 'auto', mb: 6 }}>
                            We don't just inspect quality into our parts; we build it into our processes.
                        </Typography>
                        <Grid container spacing={4} justifyContent="center" sx={{ mt: 4 }}>
                            {qualityStats.map((stat, index) => (
                                <Grid item xs={6} md={3} key={index}>
                                    <Paper elevation={0} sx={{ bgcolor: 'rgba(255,255,255,0.1)', p: 3, borderRadius: 2, backdropFilter: 'blur(5px)' }}>
                                        <Typography variant="h3" fontWeight="bold" sx={{ color: '#4fc3f7' }}>{stat.value}</Typography>
                                        <Typography variant="h6" fontWeight="bold">{stat.label}</Typography>
                                        <Typography variant="caption" sx={{ opacity: 0.7 }}>{stat.desc}</Typography>
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>
                    </motion.div>
                </Container>
            </Box>

            {/* Quality Policy Section */}
            <Container maxWidth="lg" sx={{ py: 12 }}>
                <Grid container spacing={8} alignItems="center">
                    <Grid item xs={12} md={6}>
                        <Typography variant="overline" color="primary" fontWeight="bold" sx={{ letterSpacing: 2 }}>
                            OUR PHILOSOPHY
                        </Typography>
                        <Typography variant="h3" gutterBottom fontWeight="900" sx={{ mb: 4, color: '#0d47a1' }}>
                            Zero Defect Culture
                        </Typography>
                        <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', color: 'text.secondary', lineHeight: 1.8 }}>
                            Quality is not just a department at AYDA INDUSTRIES; it is a mindset that permeates every level of our organization. From the machinist on the shop floor to the packaging team, every employee is empowered to stop the line if quality standards are not met.
                        </Typography>
                        <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', color: 'text.secondary', lineHeight: 1.8 }}>
                            Our Quality Policy is centered around <strong>Customer Satisfaction</strong> through strict adherence to specifications, timely delivery, and a commitment to <strong>Continuous Improvement</strong> of our Quality Management System (QMS).
                        </Typography>
                        <Box sx={{ mt: 4 }}>
                            <List>
                                {['Compliance with ISO 9001:2015 Standards', 'Root Cause Analysis for every deviation', 'Regular Training & Skill upgrading', 'Sustainable & Ethical manufacturing practices'].map((item, index) => (
                                    <ListItem key={index} disableGutters>
                                        <ListItemIcon>
                                            <CheckCircleIcon color="success" />
                                        </ListItemIcon>
                                        <ListItemText primary={item} primaryTypographyProps={{ variant: 'h6', fontWeight: 500 }} />
                                    </ListItem>
                                ))}
                            </List>
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Box
                            component={motion.div}
                            whileHover={{ scale: 1.02 }}
                            sx={{
                                position: 'relative',
                                borderRadius: 4,
                                overflow: 'hidden',
                                boxShadow: '0 20px 40px rgba(0,0,0,0.2)'
                            }}
                        >
                            <img src="/images/quality.png" alt="Quality Inspection" style={{ width: '100%', display: 'block' }} />
                            <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, bgcolor: 'rgba(0,0,0,0.7)', p: 2, color: 'white' }}>
                                <Typography variant="subtitle1" align="center" fontStyle="italic">
                                    "Quality means doing it right when no one is looking." - Henry Ford
                                </Typography>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
            </Container>


            {/* Process Workflow */}
            <Box sx={{ bgcolor: '#f4f6f8', py: 12 }}>
                <Container maxWidth="lg">
                    <Box textAlign="center" mb={8}>
                        <Typography variant="overline" color="secondary" fontWeight="bold">SYSTEMATIC CONTROL</Typography>
                        <Typography variant="h3" fontWeight="900" sx={{ mb: 2 }}>Total Quality Management</Typography>
                        <Typography variant="body1" color="text.secondary" maxWidth="700px" mx="auto">
                            We employ a multi-stage inspection process to ensure errors are caught and corrected at the source, preventing defective parts from moving to the next stage.
                        </Typography>
                    </Box>
                    <Grid container spacing={4}>
                        {workFlow.map((step, index) => (
                            <Grid item xs={12} md={6} key={index}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 4,
                                        height: '100%',
                                        bgcolor: 'white',
                                        borderRadius: 3,
                                        borderLeft: `6px solid #1a237e`,
                                        transition: '0.3s',
                                        '&:hover': { transform: 'translateY(-5px)', boxShadow: 6 }
                                    }}
                                >
                                    <Box display="flex" alignItems="center" mb={2}>
                                        <Box sx={{ color: 'primary.main', mr: 2 }}>{step.icon}</Box>
                                        <Typography variant="h5" fontWeight="bold">{step.title}</Typography>
                                    </Box>
                                    <Divider sx={{ mb: 2 }} />
                                    <Typography variant="body1" color="text.secondary" lineHeight={1.7}>
                                        {step.desc}
                                    </Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* Equipment Table */}
            <Box sx={{ py: 12 }}>
                <Container maxWidth="lg">
                    <Typography variant="h3" gutterBottom fontWeight="900" sx={{ mb: 1, color: '#0d47a1' }}>
                        Metrology Lab Inventory
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 6, color: 'text.secondary', maxWidth: '800px' }}>
                        Our measurement capabilities match our machining precision. We house our sensitive instruments in a dedicated, temperature-controlled environment (20°C ± 1°C) to ensure thermal stability during inspection.
                    </Typography>

                    <TableContainer component={Paper} elevation={4} sx={{ borderRadius: 2 }}>
                        <Table sx={{ minWidth: 800 }}>
                            <TableHead sx={{ bgcolor: '#263238' }}>
                                <TableRow>
                                    <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '1rem', py: 2 }}>Instrument Name</TableCell>
                                    <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '1rem', py: 2 }}>Make / Model</TableCell>
                                    <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '1rem', py: 2 }}>Range</TableCell>
                                    <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '1rem', py: 2 }}>Accuracy</TableCell>
                                    <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '1rem', py: 2 }}>Application</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {equipmentList.map((row, index) => (
                                    <TableRow key={index} sx={{ '&:nth-of-type(odd)': { bgcolor: '#f5f5f5' }, '&:hover': { bgcolor: '#e3f2fd' } }}>
                                        <TableCell component="th" scope="row" sx={{ fontWeight: 'bold', color: '#0d47a1' }}>{row.name}</TableCell>
                                        <TableCell>{row.make}</TableCell>
                                        <TableCell sx={{ fontFamily: 'monospace' }}>{row.range}</TableCell>
                                        <TableCell sx={{ fontFamily: 'monospace', color: 'success.main', fontWeight: 'bold' }}>{row.accuracy}</TableCell>
                                        <TableCell sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>{row.desc}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Container>
            </Box>

            {/* Documentation & Reports */}
            <Box sx={{ bgcolor: '#e3f2fd', py: 12 }}>
                <Container maxWidth="lg">
                    <Typography variant="h3" align="center" fontWeight="900" sx={{ mb: 8 }}>Transparency & Traceability</Typography>
                    <Grid container spacing={4}>
                        {[
                            { title: 'First Article Inspection', code: 'FAI / ISIR', desc: 'A comprehensive report validating all dimensions of the first component produced. We follow AS9102 standards for aerospace components.' },
                            { title: 'Material Test Certificates', code: 'MTC', desc: 'Chemical and physical property reports from the raw material mill, ensuring the material meets ASTM/DIN standards.' },
                            { title: 'PDI Report', code: 'PDI', desc: 'Pre-Dispatch Inspection reports covering critical dimensions and cosmetic checks for the entire batch.' },
                            { title: 'Heat Treatment Charts', code: 'HT', desc: 'Time-temperature graphs and hardness testing reports to validate the heat treatment process.' },
                            { title: 'Surface Treatment', code: 'Plating', desc: 'Coating thickness reports and salt-spray test results for anodizing, zinc plating, or other finishes.' },
                            { title: 'Corrective Action', code: 'CAPA/8D', desc: 'In the rare event of a non-conformance, a formal Root Cause Analysis and Corrective Action report is generated.' }
                        ].map((doc, i) => (
                            <Grid item xs={12} md={4} key={i}>
                                <Card sx={{ height: '100%', borderRadius: 3, transition: '0.3s', '&:hover': { transform: 'translateY(-5px)', boxShadow: 5 } }}>
                                    <CardContent sx={{ p: 4, textAlign: 'center' }}>
                                        <Box sx={{ mb: 2, display: 'inline-block', p: 1, px: 2, bgcolor: 'primary.main', color: 'white', borderRadius: 10, fontWeight: 'bold', fontSize: '0.8rem' }}>
                                            {doc.code}
                                        </Box>
                                        <Typography variant="h5" fontWeight="bold" gutterBottom>{doc.title}</Typography>
                                        <Typography variant="body2" color="text.secondary" paragraph>
                                            {doc.desc}
                                        </Typography>
                                        <Button size="small" variant="text" color="primary" sx={{ fontWeight: 'bold' }}>View Sample</Button>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* FAQ Section */}
            <Box sx={{ py: 12, bgcolor: '#fff' }}>
                <Container maxWidth="md">
                    <Typography variant="h4" fontWeight="bold" textAlign="center" mb={6}>Frequently Asked Questions</Typography>
                    {[
                        { q: 'Do you provide material certifications with every shipment?', a: 'Yes, we provide standard Mill Test Certificates (MTC) with every shipment. If you require specific third-party lab testing, we can arrange that for an additional fee.' },
                        { q: 'What is your standard general tolerance?', a: 'Our standard general tolerance for machined parts is ISO 2768-m (Medium) unless otherwise specified in your drawings. We routinely achieve much tighter tolerances (+/- 0.005mm) on critical features.' },
                        { q: 'How do you handle non-conforming parts?', a: 'Non-conforming parts are segregated immediately. We have a formal MRB (Material Review Board) process to decide rework or scrap. We ensure no defective part leaves our facility.' },
                        { q: 'Can you work with our specific quality portal?', a: 'Yes, we are experienced in working with various customer supplier portals (e.g., SAP Ariba, Coupa) to upload inspection data and shipping documents directly.' }
                    ].map((faq, index) => (
                        <Accordion key={index} disableGutters elevation={0} sx={{ borderBottom: '1px solid #eee', '&:before': { display: 'none' } }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon color="primary" />}>
                                <Typography variant="h6" fontWeight="bold" sx={{ color: '#263238' }}>{faq.q}</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Typography variant="body1" color="text.secondary" sx={{ pb: 2 }}>{faq.a}</Typography>
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </Container>
            </Box>

            {/* Certifications Block */}
            <Box sx={{ bgcolor: '#00c853', color: 'white', py: 10 }}>
                <Container maxWidth="md" sx={{ textAlign: 'center' }}>
                    <AssignmentTurnedInIcon sx={{ fontSize: 80, mb: 2, opacity: 0.9 }} />
                    <Typography variant="h3" fontWeight="900" gutterBottom>
                        Certified Excellence
                    </Typography>
                    <Typography variant="h6" paragraph sx={{ opacity: 0.9, maxWidth: '700px', mx: 'auto', mb: 4 }}>
                        We are an ISO 9001:2015 certified company, committed to the highest standards of quality management, safety, and continuous improvement.
                    </Typography>
                    <Box sx={{ display: 'inline-block', bgcolor: 'white', color: '#00c853', px: 4, py: 1, borderRadius: 10, fontWeight: 'bold' }}>
                        Certificate No: AYDA-2023-ISO
                    </Box>
                </Container>
            </Box>
        </Box>
    );
};

export default Quality;
